import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { axios } from 'api'
import useToast from 'hooks/useAlertAndToast/useToast'
import CancelConfirmationModal from 'components_v2/Events/CancelConfirmationModal'
import {
  community_event_cancel_click_event,
  community_event_cancel_success_event
} from 'gtm/pushEvents'

// Mirrors the cancel button on the event details page
// (EventDetailView.style.js `.eventCancelGroup.cancel`): white pill, grey
// border, uppercase label.
const CancelButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 35px;
  padding: 0 16px;
  background: #fff;
  color: #000;
  border: 1px solid #a3a3a3;
  border-radius: 360px;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  margin-bottom: 8px;
  cursor: pointer;

  ${(props) =>
    props.$disabled &&
    `
      cursor: default;
      opacity: 0.6;
    `}
`

// Inline cancel for "Your Upcoming Events" cards. The card is a link, so every
// handler stops propagation to avoid navigating to the detail page. Reuses the
// same confirmation modal + cancel endpoint as the detail page.
const CancelRegistration = ({
  id,
  title,
  registrationStatus,
  location,
  onEventCancelled
}) => {
  const setToast = useToast()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const gaEvent = { id, title }

  const openConfirm = (event) => {
    event?.preventDefault()
    event?.stopPropagation()
    if (submitting) return
    community_event_cancel_click_event({
      event: gaEvent,
      location,
      registrationStatus
    })
    setOpen(true)
  }

  const handleConfirm = async () => {
    if (submitting || !id) return
    setSubmitting(true)
    try {
      await axios.post(`/events/${id}/cancel`)
      // Emit before the toast / onEventCancelled refetch so a downstream
      // re-render can't pre-empt the dataLayer push.
      community_event_cancel_success_event({ event: gaEvent })
      setToast({ message: 'Your registration has been cancelled.' })
      setOpen(false)
      onEventCancelled?.(id)
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.message ||
        'Unable to cancel registration. Please try again.'
      setToast({ message, error: true })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <CancelButton onClick={openConfirm} $disabled={submitting}>
        {submitting ? 'Cancelling\u2026' : 'Cancel'}
      </CancelButton>
      <CancelConfirmationModal
        open={open}
        onClose={() => {
          if (!submitting) setOpen(false)
        }}
        onConfirm={handleConfirm}
      />
    </>
  )
}

CancelRegistration.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  registrationStatus: PropTypes.string,
  location: PropTypes.string,
  onEventCancelled: PropTypes.func
}

export default CancelRegistration
